import { useApi, SKY_API_ENDPOINT } from '@/api';
import { ProgressBar } from '@/components';
import type { ProgressBarProps } from '@/components/ProgressBar';
import { useChartTooltipSpk } from '@/hooks';
import {
  Card,
  CurrencyValue,
  DoughnutChart,
  Flex,
  formatPercentage,
  Heading,
  hexToRgb,
  TableSkeleton,
  useIsMobile
} from '@blockanalitica/ui';
import { useMemo, ReactNode } from 'react';
import { determineAssetColor } from '@/utils/colors';
import { colors } from '@/theme/colors';

type USDSBackedData = {
  category: string;
  category_name: string | null;
  backed: number;
};

type USDSBackedResponse = {
  data: USDSBackedData[];
};

interface ProgressData extends ProgressBarProps {
  text: string | ReactNode;
  symbol: string;
  percent: number;
}
export default function USDSBackedBreakdown() {
  const isMobile = useIsMobile();
  const [response, error] = useApi<USDSBackedResponse>({
    baseUrl: SKY_API_ENDPOINT,
    url: '/backing/'
  });

  const processedData: ProgressData[] | null = useMemo(() => {
    if (!response?.data || response.data.length === 0) {
      return null;
    }

    const total = response.data.reduce(
      (sum, item) => sum + Number(item.backed),
      0
    );

    const transformedData = response.data.map((item, index) => {
      const percentage = item.backed / total;

      return {
        category: item.category,
        category_name: item.category_name || item.category,
        backed: item.backed,
        percentage,
        // Add index, but is this just fixing a bug or what?
        uniqueKey: `${item.category}_${index}`
      };
    });

    // Sort data by percentage in descending order
    const sortedData = transformedData.sort(
      (a, b) => b.percentage - a.percentage
    );

    // Split into top 5 and others and transform them
    const topFive = sortedData.slice(0, 5).map((item) => ({
      text: item.category_name,
      symbol: item.uniqueKey,
      percent: item.percentage,
      additionalValue: (
        <CurrencyValue value={item.backed} options={{ currency: 'USD' }} />
      ),
      color: determineAssetColor(item.category)
    }));
    const others = sortedData.slice(5);

    // Calculate total percentage/amount for others
    const othersPercentage = others.reduce(
      (sum, item) => sum + item.percentage,
      0
    );
    const othersAmount = others.reduce(
      (sum, item) => sum + Number(item.backed),
      0
    );

    // Return top 5 plus Others if there are more than 5 items
    if (others.length > 0) {
      return [
        ...topFive,
        {
          text: 'Others',
          symbol: 'Others',
          percent: othersPercentage,
          additionalValue: (
            <CurrencyValue value={othersAmount} options={{ currency: 'USD' }} />
          ),
          color: colors.progressBarOthers,
          breakdown: others.map((item) => ({
            text: item.category_name,
            percent: item.percentage,
            additionalValue: (
              <CurrencyValue
                value={item.backed}
                options={{ currency: 'USD' }}
              />
            )
          }))
        }
      ];
    }

    return topFive;
  }, [response]);

  const customTooltip = useChartTooltipSpk(
    processedData?.map(({ text, percent, additionalValue }) => ({
      text,
      percent,
      additionalValue
    })),
    'text',
    'percent',
    'additionalValue'
  );
  const customConfig = useMemo(
    () => ({
      options: {
        layout: {
          padding: 10
        },
        plugins: {
          legend: {
            position: 'bottom' as const
          },
          tooltip: {
            enabled: false,
            position: 'nearest' as const,
            external: (context) => customTooltip(context)
          }
        }
      }
    }),
    [customTooltip]
  );

  const chartData = useMemo(() => {
    if (!processedData) {
      return;
    }

    const labels: string[] = [];
    const backgroundColor: string[] = [];
    const colorsBorder: string[] = [];
    const datasetData: number[] = [];

    processedData.forEach((row) => {
      const { r, g, b } = hexToRgb(row.color);
      // Use text (category_name) for display in chart legend
      labels.push(typeof row.text === 'string' ? row.text : String(row.text));
      backgroundColor.push(`rgba(${r}, ${g}, ${b}, 0.5)`);
      colorsBorder.push(`rgba(${r}, ${g}, ${b}, 1)`);
      datasetData.push(row.percent);
    });

    return {
      labels,
      backgroundColor,
      colorsBorder,
      datasets: [
        {
          data: datasetData,
          hoverOffset: 15
        }
      ]
    };
  }, [processedData]);

  if (error) {
    throw error;
  }
  if (!response) {
    // TODO: wrong skeleton
    return <TableSkeleton rows={10} columns={6} />;
  }

  if (!processedData || !chartData) {
    return null;
  }

  return (
    <Card flexDirection="column" gap="s">
      <Heading size="4">USDS Backed Breakdown</Heading>
      <Flex color="muted" paddingBottom="s" fontSize="-1">
        USDS is a fully collateralized stablecoin backed by a diversified
        portfolio of high-quality assets. The collateral is maintained in a
        transparent reserve to ensure at least 100% backing at all times.
      </Flex>
      <Flex gap="s">
        <Flex flexDirection="column" gap="m" flex="1">
          {processedData.map((row) => (
            <ProgressBar key={row.symbol} {...row} />
          ))}
        </Flex>

        {!isMobile ? (
          <Card flexDirection="column" gap="m" backgroundColor="transparent">
            {/* 
                DoughnutChart needs to be wrapped in Flex (technically div) in order
                for the custom tooltip to attach to the correct parent
              */}
            <Flex>
              <DoughnutChart
                config={customConfig}
                data={chartData}
                valueFormatter={formatPercentage}
              />
            </Flex>
          </Card>
        ) : null}
      </Flex>
    </Card>
  );
}
